import { useState, ReactNode } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Headphones, Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';
import { z } from 'zod';
import { supabase } from '@/integrations/supabase/client';
import { AltchaWidget } from '@/components/AltchaWidget';

const supportRequestSchema = z.object({
  subject: z.string().trim().min(1, 'Subject is required').max(100, 'Subject must be less than 100 characters'),
  message: z.string().trim().min(10, 'Please provide more detail (at least 10 characters)').max(2000, 'Message must be less than 2000 characters'),
});

interface SupportDialogProps {
  userEmail: string;
  trigger?: ReactNode | null;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function SupportDialog({ userEmail, trigger, open: controlledOpen, onOpenChange }: SupportDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = typeof controlledOpen === 'boolean';
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = (nextOpen: boolean) => {
    if (!isControlled) {
      setInternalOpen(nextOpen);
    }
    onOpenChange?.(nextOpen);
  };
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{ subject?: string; message?: string }>({});
  const [altchaToken, setAltchaToken] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    const result = supportRequestSchema.safeParse({ subject, message });
    if (!result.success) {
      const fieldErrors: { subject?: string; message?: string } = {};
      result.error.errors.forEach((err) => {
        if (err.path[0] === 'subject') fieldErrors.subject = err.message;
        if (err.path[0] === 'message') fieldErrors.message = err.message;
      });
      setErrors(fieldErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      if (!altchaToken) {
        toast.error('Please complete the verification.');
        setIsSubmitting(false);
        return;
      }
      const { data, error } = await supabase.functions.invoke('submit-support-request', {
        body: {
          subject: subject.trim(),
          message: message.trim(),
          userEmail,
          altchaToken,
        },
      });

      if (error) {
        throw error;
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to submit support request');
      }

      toast.success('Support request submitted successfully! We\'ll get back to you soon.');
      setSubject('');
      setMessage('');
      setAltchaToken(null);
      setOpen(false);
    } catch (err) {
      console.error('Support request error:', err);
      toast.error('Failed to submit support request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) {
      document.documentElement.style.pointerEvents = '';
      document.body.style.pointerEvents = '';
      setSubject('');
      setMessage('');
      setErrors({});
      setAltchaToken(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {trigger === null ? null : (
        <DialogTrigger asChild>
          {trigger ? (
            trigger
          ) : (
            <Button variant="outline" size="sm">
              <Headphones className="h-4 w-4 mr-2" />
              Support
            </Button>
          )}
        </DialogTrigger>
      )}
      <DialogContent className="cyber-card max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-2">
            <Headphones className="h-6 w-6 text-primary" />
          </div>
          <DialogTitle className="text-xl font-mono">
            Contact Support
          </DialogTitle>
          <DialogDescription>
            Need help? Our security team is here to assist you.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="support-subject">Subject</Label>
            <Input
              id="support-subject"
              placeholder="What do you need help with?"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="bg-muted/50"
              maxLength={100}
            />
            {errors.subject && (
              <p className="text-sm text-destructive">{errors.subject}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="support-message">Message</Label>
            <Textarea
              id="support-message"
              placeholder="Please describe your issue or question in detail. Include any relevant domain names or error messages."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="bg-muted/50 min-h-[150px] resize-none"
              maxLength={2000}
            />
            <div className="flex justify-between items-center">
              {errors.message ? (
                <p className="text-sm text-destructive">{errors.message}</p>
              ) : (
                <span />
              )}
              <span className="text-xs text-muted-foreground">
                {message.length}/2000
              </span>
            </div>
          </div>

          <div className="pt-4 border-t border-border">
            <div className="rounded-md border border-border/50 p-3 bg-muted/30 mb-3">
              <p className="text-xs text-muted-foreground mb-2">Please verify to submit</p>
              <AltchaWidget onToken={setAltchaToken} className="block" />
            </div>
            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send Message
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
